import { chart } from "@lib/angular/chart";
import { fragQuery, TimelineCustomSizes } from "../../../../shared-models/frag-query";
import { DateTime } from "@lib/date-time";
import { getChartLineSeperationThreshold } from "@app/old-ui/dashboard/shared";
import { measurement } from "@lib/measurement";
import { ExtendedTypeMeta } from "@lib/measurement/types";
import { MeasurementTypeToUnit } from "@lib/angular/measurement/measurement-type-to-unit";
import { Device, muckpileQuery, Vehicle } from "@models";
import { PRIMARY_COLOR } from "src/colors";
import { TiffToImageConverter } from "@app/utilities/snapshot/tiff-to-image-converter";
import { TooltipFormatterContextObject } from "highcharts";
import { CHART_LABEL_COLOR, CHART_TEXT_COLOR, CommonChartConfig } from "@app/utilities/chart-config";
import { asFiniteNumber } from "@lib/number";

interface timelineChartConfig {
    chartType: "series" | "line";
    percents: Map<number, string>;
    input: fragQuery.timeline.sieveSizesAtPercents.Response;
    particleSizeUnit: string;
    zoomingEnable: boolean;
    navigatorEnable: boolean;
    tooltipEnable: boolean;
    scrollbarEnable: boolean;
    addData?: (type: string) => void;
    plotClick?: (time: number, clickedIndex?: number) => void;
    muckpiles?: muckpileQuery.list.Response;
    vehicles?: Vehicle[];
    device?: Device
}

export const generateChartSummaryLines = (percents: Map<number, string>, input: fragQuery.timeline.sieveSizesAtPercents.Response) => {
    const lines: chart.line.Series[] = [];
    if (input.summary) {
        for (const [percent, color] of percents) {
            let value = input.summary.dvalues[percent];
            if (value !== undefined) {
                lines.push({
                    type: 'line',
                    name: null,
                    data: [
                        [input.begin, value],
                        [input.end, value],
                    ],
                    color,
                    width: 1,
                    dash: [4],
                    includeInRange: [true, true],
                });
            }
        }
    }
    return lines;
}

export type DataPoint = [
    DateTime,
    number,
    fragQuery.timeline.sieveSizesAtPercents.Interval
];

export const generateChartLines = (percents: Map<number, string>, input: fragQuery.timeline.sieveSizesAtPercents.Response): chart.line.Series[] => {
    if (input === undefined) {
        return undefined;
    }
    if (!(input?.intervals?.length > 0)) {
        return [];
    }
    percents = new Map(percents);
    const series = generateChartSummaryLines(percents, input);

    const seperationThreshold = getChartLineSeperationThreshold(input.intervals);

    series.push(
        ...input.percents
            .sort((a, b) => a - b)
            .map((percent, i): chart.line.Series => {
                if (!percents.has(percent)) return null;
                const data: DataPoint[] = [];
                let prevInterval: fragQuery.timeline.sieveSizesAtPercents.Interval;
                for (const interval of input.intervals) {
                    const y = (interval.data[i] ?? 0);
                    if (!prevInterval) {
                        data.push([interval.begin, y, interval]);
                    } else if (
                        prevInterval.end.getTime() - interval.begin.getTime() >=
                        seperationThreshold
                    ) {
                        data.push([prevInterval.end, 0, interval]);
                        data.push([interval.begin, 0, interval]);
                    }
                    const x = interval.begin.lerp(interval.end, 0.5);
                    data.push([x, y, interval]);
                    prevInterval = interval;
                }

                return {
                    type: 'line',
                    name: 'D' + percent,
                    data,
                    color: percents.get(percent),
                    fill: true,
                    includeInRange: [false, true],
                };
            })
            .filter((v) => !!v)
            .reverse()
    );
    return series;
}

export const generateChartSeries = (percents: Map<number, string>, input: fragQuery.timeline.sieveSizesAtPercents.Response): chart.line.Series[] => {

    if (input === undefined) {
        return undefined;
    }

    if (!(input?.intervals?.length > 0)) {
        return [];
    }

    percents = new Map(percents);
    const series = generateChartSummaryLines(percents, input);

    series.push(
        ...input.percents
            .sort((a, b) => a - b)
            .map((percent, i): chart.line.Series => {
                if (!percents.has(percent)) return null;
                const data = input.intervals.map(
                    (interval) =>
                        <DataPoint>[
                            interval.begin,
                            (interval.data[i] ?? 0),
                            interval,
                        ]
                );
                return {
                    type: 'occurances',
                    name: 'D' + percent,
                    data,
                    color: percents.get(percent),
                    includeInRange: [false, true],
                };
            })
            .filter((v) => !!v)
            .reverse()
    );
    return series;
}

export const generateQuantityChartLines = (input: fragQuery.timeline.quantity.Response) => {
    if (input === undefined) {
        return undefined;
    }
    if (!(input?.intervals?.length > 0)) {
        return [];
    }

    const separationThreshold = getChartLineSeperationThreshold(input.intervals);
    const data: [DateTime, number][] = [];
    for (const [a, b] of input.intervals.pairs()) {
        const valueA = a.data;
        const valueB = b.data;

        data.push([a.end, valueA]);
        if (Math.abs(a.end.getTime() - b.begin.getTime()) >= separationThreshold) {
            data.push([a.end, 0]);
            data.push([b.begin, 0]);
            data.push([b.begin, valueB]);
        }
    }

    const last = input.intervals.at(-1);
    const lastValue = last.data;
    data.push([last.end, lastValue]);

    const lines: chart.line.Series[] = [];
    lines.push({
        type: 'line',
        name: 'Quantity',
        color: 'lightblue',
        fill: true,
        data,
    });
    if (lines[0].data.length === 1) {
        lines[0].data.unshift([input.intervals[0].begin, lines[0].data[0][1]]);
    }
    return lines;
}

export const generatePayloadChartineForPdf = (input: fragQuery.timeline.quantity.Response) => {
    if (input === undefined) {
        return undefined;
    }
    if (!(input?.intervals?.length > 0)) {
        return [];
    }

    const separationThreshold = getChartLineSeperationThreshold(input.intervals);
    const data: [DateTime, number][] = [];
    for (const [a, b] of input.intervals.pairs()) {
        const valueA = a.payload;
        const valueB = b.payload;

        data.push([a.end, valueA]);
        if (Math.abs(a.end.getTime() - b.begin.getTime()) >= separationThreshold) {
            data.push([a.end, 0]);
            data.push([b.begin, 0]);
            data.push([b.begin, valueB]);
        }
    }

    const last = input.intervals.at(-1);
    const lastValue = last.payload;
    data.push([last.end, lastValue]);

    const lines: chart.line.Series[] = [];
    lines.push({
        type: 'line',
        name: 'Quantity',
        color: 'lightgreen',
        fill: true,
        data,
    });
    if (lines[0].data.length === 1) {
        lines[0].data.unshift([input.intervals[0].begin, lines[0].data[0][1]]);
    }
    return lines;
}

export const generateQuantityChartSeries = (input: fragQuery.timeline.quantity.Response) => {
    if (input === undefined) {
        return undefined;
    }
    if (!(input?.intervals?.length > 0)) {
        return [];
    }

    const bars: chart.line.Series[] = [];
    bars.push({
        type: 'occurances',
        name: 'Quantity',
        color: 'lightblue',
        data: input.intervals.map(interval => [interval.begin, interval.data]),
    });
    return bars;
}

export type TimeLineDataPoint = [
    number,
    number
];

const _getAlertIcon = (interval: fragQuery.timeline.sieveSizesAtPercents.Interval): string => {
    switch (true) {
        case !!interval.thresholdCount:
            return `<img src="./../../../../assets/mat-icons/warn-red.svg" width="16" height="16"/>`;
        case !!interval.slabs:
            return `<img src="./../../../../assets/mat-icons/warn-orange.svg" width="16" height="16"/>`;
        default:
            return `<img src="./../../../../assets/mat-icons/warn-orange.svg" width="16" height="16"/>`;
    }
}

const darkGray = 'rgba(15, 15, 15, 0.3)'
const lightGray = 'rgba(200, 200, 200, 0.3)'

const _createEmptyBeltPlotBand = (from: number, to: number, type: 'blackBelt' | 'beltOff'): Highcharts.XAxisPlotBandsOptions => {
    const isBlackBelt = type === 'blackBelt';
    return {
        from,
        to,
        color: isBlackBelt ? darkGray : lightGray,
        label: isBlackBelt
            ? undefined
            : {
                useHTML: true,
                text: `<div class="plotband-circle-pin">
                <img src="./../../../../assets/mat-icons/empty _belt_data.svg" width="16" height="16" style="vertical-align: middle;" />
            </div>`,
                style: { color: '#606060' }
            }
    };
};

const _createAlertMarkerPoint = (
    x: number,
    y: number,
    interval: fragQuery.timeline.sieveSizesAtPercents.Interval
): Highcharts.PointOptionsObject => ({
    x,
    y,
    dataLabels: {
        enabled: true,
        useHTML: true,
        formatter: () => _getAlertIcon(interval),
        y: -20
    }
});


const _generateEmptyTimelinePlotBands = (
    input: fragQuery.timeline.sieveSizesAtPercents.Response
): Highcharts.XAxisPlotBandsOptions[] => {
    const plotBands: Highcharts.XAxisPlotBandsOptions[] = [];

    const intervals = input.intervals;
    const timeLineStart = input.begin.valueOf();
    const timeLineEnd = input.end.valueOf();

    intervals.sort((a, b) => a.begin.valueOf() - b.begin.valueOf());

    // Add plotband for empty interval in very start of the timeline chart.
    if (intervals[0].begin.valueOf() > timeLineStart) {
        plotBands.push(_createEmptyBeltPlotBand(timeLineStart, intervals[0].begin.valueOf(), 'beltOff'));
    }

    // Add plotBands for internal gaps > 2 mins
    const emptyGapBetweenInterval = 3 * 60 * 1000;
    for (let i = 0; i < intervals.length - 1; i++) {
        const gap = intervals[i + 1].begin.valueOf() - intervals[i].end.valueOf();
        if (gap > emptyGapBetweenInterval) {
            plotBands.push(_createEmptyBeltPlotBand(intervals[i].end.valueOf(), intervals[i + 1].begin.valueOf(), 'beltOff'));
        }
    }

    // Add plotband for empty interval in very end of the timeline chart.
    const lastIntervalEnd = intervals[intervals.length - 1].end.valueOf();
    if (lastIntervalEnd < timeLineEnd) {
        plotBands.push(_createEmptyBeltPlotBand(lastIntervalEnd, timeLineEnd, 'beltOff'));
    }

    for (const interval of intervals) {
        const hasData = Array.isArray(interval.data) && interval.data.some(Boolean);

        if (!hasData) {
            plotBands.push(_createEmptyBeltPlotBand(interval.begin.valueOf(), interval.end.valueOf(), 'blackBelt'));
        }
    }

    return plotBands;
};

export const generateTimelineChartSummaryLines = (percents: Map<number, string>, input: fragQuery.timeline.sieveSizesAtPercents.Response) => {
    const lines: Array<Highcharts.SeriesOptionsType> = [];
    if (input.summary) {
        for (const [percent, color] of percents) {
            let value = input.summary.dvalues[percent];
            if (value !== undefined) {
                lines.push({
                    type: 'line',
                    name: null,
                    data: [
                        [input.begin.valueOf(), value],
                        [input.end.valueOf(), value],
                    ],
                    color,
                    lineWidth: 2,
                    dashStyle: 'ShortDash',
                    enableMouseTracking: false
                });
            }
        }
    }
    return lines;
}

export const generateTimelineChartLines = (timeLineChartParam: timelineChartConfig): Highcharts.Options => {
    const { chartType, input, particleSizeUnit, zoomingEnable, navigatorEnable, tooltipEnable, scrollbarEnable, addData, plotClick, muckpiles, vehicles, device } = timeLineChartParam
    let { percents } = timeLineChartParam

    if (input === undefined) {
        return undefined;
    }
    if (!(input?.intervals?.length > 0)) {
        return null;
    }
    percents = new Map(percents);
    const percentsNum = Array.from(percents.keys());
    const maxPercent = Math.max(...percentsNum);
    const alertIconMarkerPoints: Highcharts.PointOptionsObject[] = [];
    const series: Array<Highcharts.SeriesOptionsType> = [];
    const isConveyorDevice = device?.type === 'conveyor';
    const plotBands: Highcharts.XAxisPlotBandsOptions[] = isConveyorDevice ? _generateEmptyTimelinePlotBands(input) : [];
    const seperationThreshold = getChartLineSeperationThreshold(input.intervals);
    let debounceTimeout: ReturnType<typeof setTimeout>;
    let lastHoveredPoint: Highcharts.Point;

    series.push(...generateTimelineChartSummaryLines(percents, input));

    series.push(
        ...Array.from(percents.keys())
            .filter((percent) => input.percents.includes(percent) && percent !== 0)
            .sort((a, b) => a - b)
            .map((percent, i): Highcharts.SeriesOptionsType => {
                if (!percents.has(percent)) return null;
                const index = input.percents.indexOf(percent);
                if (chartType === 'line') {
                    const data: TimeLineDataPoint[] = [];
                    let prevInterval: fragQuery.timeline.sieveSizesAtPercents.Interval;
                    for (const interval of input.intervals) {
                        const y = (interval.data[index] ?? 0);
                        if (!prevInterval) {
                            data.push([interval.begin.valueOf(), y]);
                        } else if (
                            prevInterval.end.getTime() - interval.begin.getTime() >=
                            seperationThreshold
                        ) {
                            data.push([prevInterval.end.valueOf(), 0]);
                            data.push([interval.begin.valueOf(), 0]);
                        }
                        // This represents the center of the interval and is used as the X coordinate
                        const x = interval.begin.valueOf();
                        data.push([x, y]);
                        prevInterval = interval;
                        // Add alert icon marker if threshold or slabs presents
                        if (maxPercent === percent && (interval.thresholdCount || interval.slabs)) {
                            alertIconMarkerPoints.push(_createAlertMarkerPoint(x, y, interval));
                        }
                    }

                    return {
                        type: 'spline',
                        name: 'D' + percent,
                        data,
                        color: percents.get(percent),
                        lineWidth: 4,
                        point: {
                            events: {
                                click: function (event) {
                                    if (plotClick) {
                                        plotClick(this.x, this.index - 1)
                                    }
                                }
                            }
                        }
                    };
                } else {
                    const data: Highcharts.PointOptionsObject[] = [];
                    const index = input.percents.indexOf(percent);

                    for (const interval of input.intervals) {
                        const y = (index > 1 ? interval.data[index] - interval.data[index - 1] : interval.data[index]) ?? 0;

                        const point: Highcharts.PointOptionsObject = {
                            x: interval.begin.valueOf(),
                            y,
                        };

                        // Add threshold and slabs indicators using alert icon
                        if (maxPercent === percent && (interval.thresholdCount || interval.slabs)) {
                            point.dataLabels = {
                                enabled: true,
                                useHTML: true,
                                formatter: function () {
                                    return _getAlertIcon(interval);
                                },
                                y: -70
                            };
                        }
                        data.push(point);
                    }

                    return {
                        type: 'column',
                        name: 'D' + percent,
                        data,
                        grouping: false,
                        dataGrouping: {
                            enabled: false
                        },
                        color: percents.get(percent),
                        point: {
                            events: {
                                click: function (event) {
                                    if (plotClick) {
                                        plotClick(this.x, this.index - 1)
                                    }
                                }
                            }
                        }
                    };
                }
            })
            .filter((v) => !!v)
            .reverse()
    );

    if (alertIconMarkerPoints.length > 0) {
        series.push({
            type: 'scatter',
            name: 'Threshold Marker',
            data: alertIconMarkerPoints,
            enableMouseTracking: false,
            marker: {
                symbol: 'circle',
                radius: 0
            },
        });
    }

    let options: Highcharts.Options = {
        rangeSelector: {
            enabled: false
        },
        credits: {
            enabled: false
        },
        chart: {
            backgroundColor: 'transparent',
            zooming: {
                mouseWheel: {
                    enabled: zoomingEnable
                }
            },
            events: {
                click: function (e: any) {
                    if (this.hoverPoint) {
                        const clickedXValue = this.hoverPoint.x;
                        const clickedPointIndex = this.hoverPoint.index;
                        plotClick(clickedXValue, clickedPointIndex - 1);
                    } else {
                        plotClick(e.xAxis[0].value);
                    }
                }
            }
        },
        exporting: {
            enabled: false
        },
        navigator: {
            enabled: navigatorEnable
        },
        plotOptions: {
            series: {
                showInNavigator: true,
                dataLabels: [{
                    color: CHART_LABEL_COLOR
                }]
            },
            column: {
                showInNavigator: true,
                stacking: 'normal',
                grouping: false,
                dataGrouping: {
                    enabled: false
                }
            }
        },
        responsive: {
            rules: [{
                chartOptions: {
                    plotOptions: {
                        series: {
                            showInNavigator: true,
                            dataLabels: [{
                                color: CHART_LABEL_COLOR
                            }]
                        },
                        column: {
                            showInNavigator: true,
                            stacking: 'normal',
                            pointWidth: 1,
                            pointPadding: 1,
                            grouping: false,
                            dataGrouping: {
                                enabled: false
                            },
                        }
                    },
                },
                condition: {
                    callback: function () {
                        // Get the visible range from the xAxis
                        const xAxis = this.xAxis[0];
                        const min = xAxis.min;
                        const max = xAxis.max;
                        // Calculate visible points within the current view for all series
                        const visiblePoints = this.series.reduce((sum, series) => {
                            if (series.visible && series.data) {  // Ensure series is visible and has data
                                return sum + series.data.filter(point =>
                                    point && point.x >= min && point.x <= max
                                ).length;
                            }
                            return sum;
                        }, 0);
                        // Return true if visible points exceed 50
                        return visiblePoints > 1000;
                    }
                }
            }],
        },
        tooltip: {
            shared: false,
            split: true,
            useHTML: true,
            enabled: tooltipEnable,
            formatter: function () {
                // Clear the previous timeout to prevent multiple calls
                if (debounceTimeout) {
                    clearTimeout(debounceTimeout);
                }

                // Check if the tooltip on the latest hover point
                if (!lastHoveredPoint || lastHoveredPoint.x !== this.point.x) {
                    lastHoveredPoint = this.point;
                    const interval = input.intervals[this.point.index - 1];

                    // Set a new timeout
                    debounceTimeout = setTimeout(() => {
                        _generateTooltipContent(interval, isConveyorDevice, muckpiles, vehicles);
                    }, 500);
                }
                // Return the placeholder content immediately
                return _generateTooltipContent(input.intervals[this.point.index - 1], isConveyorDevice, muckpiles, vehicles, true);
            }
        },
        time: {
            timezoneOffset: new Date().getTimezoneOffset()
        },
        scrollbar: CommonChartConfig.scrollbar(scrollbarEnable),
        xAxis: {
            title: {
                text: 'Time',
                style: {
                    fontSize: '1em',
                    color: CHART_LABEL_COLOR
                },
            },
            labels: {
                style: {
                    color: CHART_LABEL_COLOR
                }
            },
            // to prevent timeline axis from auto shrinking when data is not present for a timestamp
            breaks: [{
                breakSize: 1000
            }],
            tickInterval: 3600 * 1000,
            events: {
                afterSetExtremes: function (event) {
                    if (addData) {
                        if (event.trigger && event.userMin === event.dataMin) {
                            addData('begin')
                        }
                        else if (event.trigger && event.dataMax === event.userMax) {
                            addData('end')
                        }
                    }
                }
            },
            plotBands: plotBands
        },
        yAxis: CommonChartConfig.yAxis(`Particle Size (${particleSizeUnit})`),
        series: series
    };

    return options;
}

export const adjustColorOpacity = (color: string, opacity: number): string => {
    if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    if (color.startsWith('rgb')) {
        return color.replace(/rgba?\(([^)]+)\)/, (_, values) => {
            const [r, g, b] = values.split(',').map(Number);
            return `rgba(${r}, ${g}, ${b}, ${opacity})`;
        });
    }
    return color;
};

export const generateCustomTimelineChartLines = (
    type: 'range' | 'size',
    chartType: "series" | "line",
    sizes: Map<number, TimelineCustomSizes>,
    input: fragQuery.timeline.sieveSizesAtPercents.Response,
    particleSizeUnitSystem: string,
    zoomingEnable: boolean,
    navigatorEnable: boolean,
    scrollbarEnable: boolean,
    addData?: (type: string) => void,
    plotClick?: (time: number, clickedPointIndex?: number) => void,
    opacity: number = 0.6
): Highcharts.Options => {
    let debounceTimeout: ReturnType<typeof setTimeout>;
    let lastHoveredX: Highcharts.Point;

    try {
        if (input === undefined) {
            return undefined;
        }
        if (!(input?.intervals?.length > 0)) {
            return null;
        }

        let inputSizes: number[] = Array.from(sizes.keys());
        if (type == 'range') {
            inputSizes.push(1000);
        }

        const series: Array<Highcharts.SeriesOptionsType> = [];

        const unitSystem = particleSizeUnitSystem as keyof ExtendedTypeMeta;
        const measurementTypeToUnit = new MeasurementTypeToUnit();
        const unit = measurementTypeToUnit.getUnitsForSystem(unitSystem).particleSize;
        let prevSize = 0;


        series.push(
            ...inputSizes
                .map((size, i): Highcharts.SeriesOptionsType => {
                    let upperSizeLabel;
                    let lowerSizeLabel;
                    const baseColor = sizes.get(size)?.color ?? 'red';
                    const color = adjustColorOpacity(baseColor, opacity);
                    const label = sizes.get(size)?.label ?? '';
                    if (i < inputSizes.length - 1) {
                        upperSizeLabel = sizes.get(size)?.label;
                        lowerSizeLabel = sizes.get(inputSizes[i - 1])?.label;
                    } else {
                        lowerSizeLabel = sizes.get(prevSize)?.label;
                    }

                    let lineName;
                    prevSize = size;
                    if (type == 'size') {
                        lineName = (label && label !== 'Custom')
                            ? '< ' + measurement.convert(size, 'meter', unit).toFixed(2) + `${measurement.abbreviation(unit)}` + ` (${label})`
                            : '< ' + measurement.convert(size, 'meter', unit).toFixed(2) + `${measurement.abbreviation(unit)}`;
                    } else {
                        let upperSize;
                        let lowerSize;
                        if (i == 0) {
                            lowerSize = '<';
                            upperSize = (upperSizeLabel != 'Custom')
                                ? upperSizeLabel
                                : measurement.convert((i == inputSizes.length - 1) ? inputSizes[i - 1] : inputSizes[i], 'meter', unit).toFixed(2) + `${measurement.abbreviation(unit)}`;
                        } else if (i == inputSizes.length - 1) {
                            lowerSize = '>';
                            upperSize = (lowerSizeLabel != 'Custom')
                                ? lowerSizeLabel
                                : measurement.convert((i == inputSizes.length - 1) ? inputSizes[i - 1] : inputSizes[i], 'meter', unit).toFixed(2) + `${measurement.abbreviation(unit)}`;
                        } else {
                            upperSize = (upperSizeLabel != 'Custom')
                                ? upperSizeLabel
                                : measurement.convert((i == inputSizes.length - 1) ? inputSizes[i - 1] : inputSizes[i], 'meter', unit).toFixed(2) + `${measurement.abbreviation(unit)}`;
                            lowerSize = (lowerSizeLabel != 'Custom')
                                ? lowerSizeLabel
                                : measurement.convert(inputSizes[i - 1], 'meter', unit).toFixed(2) + `${measurement.abbreviation(unit)}`;
                        }

                        lineName = lowerSize + '-' + upperSize;
                    }

                    if (chartType === 'series' && type === 'range') {
                        const data = input.intervals.map((interval) => {
                            const y =
                                i === 0
                                    ? interval.customSizePercents[i]
                                    : i === inputSizes.length - 1
                                        ? 100 - (interval.customSizePercents[i - 1] ?? 0)
                                        : (interval.customSizePercents[i] ?? 0) - (interval.customSizePercents[i - 1] ?? 0)
                                ;
                            const x = interval.begin.lerp(interval.end, 0.5).valueOf();
                            return [x, y] as TimeLineDataPoint;
                        });

                        return {
                            type: 'area',
                            name: lineName,
                            data,
                            color,
                            fillOpacity: opacity,
                            stacking: 'percent',
                            lineWidth: 1,
                            marker: {
                                enabled: false,
                            },
                            point: {
                                events: {
                                    click: function (event) {
                                        if (plotClick) {
                                            const clickedXValue = this.series.xAxis.toValue(event.chartX);
                                            plotClick(clickedXValue);
                                        }
                                    },
                                },
                            },
                        };
                    } else if (chartType === 'line') {
                        const data: TimeLineDataPoint[] = input.intervals.map((interval) => {
                            const y =
                                type === 'range'
                                    ? i === 0
                                        ? interval?.customSizePercents?.[i]
                                        : i === inputSizes.length - 1
                                            ? 100 - (interval.customSizePercents[i - 1] ?? 0)
                                            : (interval.customSizePercents[i] ?? 0) - (interval.customSizePercents[i - 1] ?? 0)
                                    : interval?.customSizePercents?.[i] ?? 0;

                            const x = interval.begin.lerp(interval.end, 0.5).valueOf();
                            return [x, y];
                        });

                        return {
                            type: 'spline',
                            name: lineName,
                            data,
                            color,
                            lineWidth: 4,
                            point: {
                                events: {
                                    click: function (event) {
                                        if (plotClick) {
                                            const clickedXValue = this.series.xAxis.toValue(event.chartX);
                                            plotClick(clickedXValue);
                                        }
                                    },
                                },
                            },
                        };
                    } else {
                        const data = input.intervals.map((interval) => {
                            const y =
                                i === 0
                                    ? interval.customSizePercents[i]
                                    : interval.customSizePercents[i] - interval.customSizePercents[i - 1];
                            const x = interval.begin.valueOf();
                            return [x, y] as TimeLineDataPoint;
                        });

                        return {
                            type: 'column',
                            name: lineName,
                            data,
                            color,
                            stacking: 'normal',
                            point: {
                                events: {
                                    click: function (event) {
                                        if (plotClick) {
                                            const clickedXValue = this.series.xAxis.toValue(event.chartX);
                                            plotClick(clickedXValue);
                                        }
                                    },
                                },
                            },
                        };
                    }
                })
                .filter((v) => !!v)
                .reverse()
        );

        let options: Highcharts.Options = {
            rangeSelector: {
                enabled: false
            },

            credits: {
                enabled: false
            },

            chart: {
                backgroundColor: 'transparent',
                zooming: {
                    mouseWheel: {
                        enabled: zoomingEnable
                    }
                },
                events: {
                    click: function (e: any) {
                        plotClick(e.xAxis[0].value);
                    }
                },
            },
            exporting: {
                enabled: false
            },
            navigator: {
                enabled: navigatorEnable
            },
            plotOptions: {
                series: {
                    showInNavigator: true,
                    dataLabels: [{
                        color: CHART_LABEL_COLOR
                    }]
                },
                column: {
                    showInNavigator: true,
                    stacking: 'normal',
                    grouping: false,
                    dataGrouping: {
                        enabled: false
                    }
                }
            },
            responsive: {
                rules: [{
                    chartOptions: {
                        plotOptions: {
                            series: {
                                showInNavigator: true,
                                dataLabels: [{
                                    color: CHART_LABEL_COLOR
                                }]
                            },
                            column: {
                                showInNavigator: true,
                                stacking: 'normal',
                                pointWidth: 1,
                                pointPadding: 1,
                                grouping: false,
                                dataGrouping: {
                                    enabled: false
                                },
                            }
                        },
                    },
                    condition: {
                        callback: function () {
                            const xAxis = this.xAxis[0];
                            const min = xAxis.min;
                            const max = xAxis.max;
                            const visiblePoints = this.series.reduce((sum, series) => {
                                if (series.visible && series.data) {
                                    return sum + series.data.filter(point =>
                                        point && point.x >= min && point.x <= max
                                    ).length;
                                }
                                return sum;
                            }, 0);

                            return visiblePoints > 750;
                        }
                    }
                }],
            },
            tooltip: {
                shared: true,
                split: false,
                useHTML: true,
                formatter: function () {
                    if (debounceTimeout) {
                        clearTimeout(debounceTimeout);
                    }

                    // Check if the tooltip on the latest hover point
                    if (!lastHoveredX || lastHoveredX.x !== this.point.x) {
                        lastHoveredX = this.point;
                        const interval = input.intervals[this.point.index];

                        // Set a new timeout
                        debounceTimeout = setTimeout(() => {
                            const chart_type = this.series.userOptions.type;
                            _createTooltipContentHtml(interval, chart_type, this.points);
                        }, 500);
                    }
                    // Return the placeholder content immediately
                    return _createTooltipContentHtml(input.intervals[this.point.index], this.series.userOptions.type, this.points, true);
                }
            },
            time: {
                timezoneOffset: new Date().getTimezoneOffset()
            },
            scrollbar: CommonChartConfig.scrollbar(scrollbarEnable),
            xAxis: {
                title: {
                    text: 'Time',
                    style: {
                        fontSize: '1em',
                        color: CHART_LABEL_COLOR
                    },
                },
                labels: {
                    style: {
                        color: CHART_LABEL_COLOR
                    }
                },
                breaks: [{
                    breakSize: 750
                }],
                tickInterval: 3600 * 1000,
                events: {
                    afterSetExtremes: function (event) {
                        if (addData) {
                            if (event.trigger && event.userMin === event.dataMin) {
                                addData('begin');
                            } else if (event.trigger && event.dataMax === event.userMax) {
                                addData('end');
                            }
                        }
                    }
                }
            },
            yAxis: CommonChartConfig.yAxis('Percent %'),
            legend: CommonChartConfig.legend(),
            series: series
        };

        return options;
    } catch (error) {
        console.error("Error generating chart options:", error);
        return null;
    }
};

export const generateTimelineQualtityChartLines = (
    chartType: "series" | "line",
    input: fragQuery.timeline.quantity.Response,
    addData: (type: string) => void,
    title: string,
    zoomingEnable: boolean,
    navigatorEnable: boolean,
    scrollbarEnable: boolean,
    tooltipEnable: boolean,
): Highcharts.Options => {
    if (input === undefined) {
        return undefined;
    }
    if (!(input?.intervals?.length > 0)) {
        return null;
    }

    const separationThreshold = getChartLineSeperationThreshold(input.intervals);
    const data: [number, number][] = [];
    for (const [a, b] of input.intervals.pairs()) {
        const valueA = a.data;
        const valueB = b.data;

        data.push([a.end.valueOf(), valueA]);
        if (Math.abs(a.end.getTime() - b.begin.getTime()) >= separationThreshold) {
            data.push([a.end.valueOf(), 0]);
            data.push([b.begin.valueOf(), 0]);
            data.push([b.begin.valueOf(), valueB]);
        }
    }

    const last = input.intervals.at(-1);
    const lastValue = last.data;
    data.push([last.end.valueOf(), lastValue]);

    if (data.length === 1) {
        data.unshift([input.intervals[0].begin.valueOf(), data[0][1]]);
    }

    const series: Array<Highcharts.SeriesOptionsType> = [];
    chartType === 'line'
        ? series.push({
            type: 'spline',
            name: 'Quantity',
            color: 'lightblue',
            data,
        })
        : series.push({
            type: 'column',
            name: 'Quantity',
            color: 'lightblue',
            data,
        });

    let options: Highcharts.Options = {
        rangeSelector: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        chart: {
            backgroundColor: 'transparent',
            zooming: {
                mouseWheel: {
                    enabled: zoomingEnable,
                },
            },
        },
        exporting: {
            enabled: false,
        },
        navigator: {
            enabled: navigatorEnable,
        },
        plotOptions: {
            series: {
                showInNavigator: true,
            },
        },
        time: {
            timezoneOffset: new Date().getTimezoneOffset(),
        },
        scrollbar: CommonChartConfig.scrollbar(scrollbarEnable),
        tooltip: {
            enabled: tooltipEnable,
        },
        xAxis: {
            title: {
                text: 'Time',
                style: {
                    fontSize: '1em',
                    color: CHART_LABEL_COLOR,
                },
            },
            labels: {
                style: {
                    color: CHART_LABEL_COLOR,
                },
            },
            breaks: [
                {
                    breakSize: 1000,
                },
            ],
            events: {
                afterSetExtremes: function (event) {
                    if (event.trigger && event.userMin === event.dataMin) {
                        addData('begin');
                    } else if (event.trigger && event.dataMax === event.userMax) {
                        addData('end');
                    }
                },
            },
        },
        yAxis: CommonChartConfig.yAxis(title),
        legend: CommonChartConfig.legend(),
        series: series,
    };

    return options;
};

export const generateOversizeCountTimelineChartLines = (
    chartType: "series" | "line",
    input: fragQuery.timeline.sieveSizesAtPercents.Response,
    addData: (type: string) => void,
    plotClick: (time: number, clickedPointIndex?: number) => void,
    title: string,
    zoomingEnable: boolean,
    navigatorEnable: boolean,
    scrollbarEnable: boolean,
    tooltipEnable: boolean,
): Highcharts.Options => {
    if (input === undefined) {
        return undefined;
    }
    if (!(input?.intervals?.length > 0)) {
        return null;
    }

    let debounceTimeout: ReturnType<typeof setTimeout>;
    let lastHoveredPoint: Highcharts.Point;
    const separationThreshold = getChartLineSeperationThreshold(input.intervals);
    const data: [number, number][] = [];
    let prevInterval: fragQuery.timeline.sieveSizesAtPercents.Interval | undefined;

    for (const interval of input.intervals) {
        const y = interval.oversizecount ?? 0;

        if (!prevInterval) {
            data.push([interval.begin.valueOf(), y]);
        } else if (prevInterval.end.getTime() - interval.begin.getTime() >= separationThreshold) {
            data.push([prevInterval.end.valueOf(), 0]);
            data.push([interval.begin.valueOf(), 0]);
        }

        // Main data point (use begin as X)
        const x = interval.begin.valueOf();
        data.push([x, y]);
        prevInterval = interval;
    }

    // Add final end point if needed
    const last = input.intervals.at(-1);
    if (last) {
        data.push([last.end.valueOf(), last.oversizecount ?? 0]);
    }

    // If only one point, duplicate start to make a valid line
    if (data.length === 1) {
        data.unshift([input.intervals[0].begin.valueOf(), data[0][1]]);
    }

    const series: Array<Highcharts.SeriesOptionsType> = [];

    if (chartType === 'line') {
        series.push({
            type: 'spline',
            name: 'Oversize Rock Count',
            color: PRIMARY_COLOR,
            data,
            lineWidth: 4,
            point: {
                events: {
                    click: function (event) {
                        if (plotClick) {
                            plotClick(this.x, this.index - 1);
                        }
                    }
                }
            }
        });
    } else {
        const data: Highcharts.PointOptionsObject[] = [];

        for (const interval of input.intervals) {
            const y = interval.oversizecount ?? 0;
            const point: Highcharts.PointOptionsObject = {
                x: interval.begin.valueOf(),
                y,
            };
            data.push(point);
        }

        series.push({
            type: 'column',
            name: 'Oversize Rock Count',
            color: PRIMARY_COLOR,
            data: data,
            grouping: false,
            dataGrouping: { enabled: false },
            point: {
                events: {
                    click: function (event) {
                        if (plotClick) {
                            plotClick(this.x, this.index - 1);
                        }
                    }
                }
            }
        });
    }

    let options: Highcharts.Options = {
        rangeSelector: { enabled: false },
        credits: { enabled: false },
        chart: {
            backgroundColor: 'transparent',
            zooming: {
                mouseWheel:
                    { enabled: zoomingEnable }
            },
            events: {
                click: function (e: any) {
                    if (this.hoverPoint) {
                        const clickedXValue = this.hoverPoint.x;
                        const clickedPointIndex = this.hoverPoint.index;
                        plotClick(clickedXValue, clickedPointIndex - 1);
                    } else {
                        plotClick(e.xAxis[0].value);
                    }
                }
            },
        },
        exporting: { enabled: false },
        navigator: { enabled: navigatorEnable },
        plotOptions: CommonChartConfig.plotOptions(navigatorEnable),
        time: { timezoneOffset: new Date().getTimezoneOffset() },
        scrollbar: CommonChartConfig.scrollbar(scrollbarEnable),
        tooltip: {
            shared: false,
            split: true,
            useHTML: true,
            enabled: tooltipEnable,
            formatter: function () {
                // Clear the previous timeout to prevent multiple calls
                if (debounceTimeout) {
                    clearTimeout(debounceTimeout);
                }
                // Check if the tooltip on the latest hover point
                if (!lastHoveredPoint || lastHoveredPoint.x !== this.point.x) {
                    lastHoveredPoint = this.point;
                    const interval = input.intervals[this.point.index - 1];

                    // Set a new timeout
                    debounceTimeout = setTimeout(() => {
                        _createOversizeRockTooltipContent(interval);
                    }, 500);
                }
                // Return the placeholder content immediately
                return _createOversizeRockTooltipContent(input.intervals[this.point.index - 1], true);
            }
        },
        xAxis: {
            title: { text: 'Time', style: { fontSize: '1em', color: CHART_LABEL_COLOR } },
            labels: { style: { color: CHART_LABEL_COLOR } },
            breaks: [{ breakSize: 1000 }],
            events: {
                afterSetExtremes: function (event) {
                    if (event.trigger && event.userMin === event.dataMin) {
                        addData('begin');
                    } else if (event.trigger && event.dataMax === event.userMax) {
                        addData('end');
                    }
                },
            },
        },
        yAxis: CommonChartConfig.yAxis('Oversize Rock Count'),
        legend: CommonChartConfig.legend(),
        series: series,
    };

    return options;
};

export const generatePayloadTimelineChartLines = (
    chartType: "series" | "line",
    input: fragQuery.timeline.quantity.Response,
    addData: (type: string) => void,
    title: string,
    zoomingEnable: boolean,
    navigatorEnable: boolean,
    scrollbarEnable: boolean,
    tooltipEnable: boolean,
): Highcharts.Options => {
    if (input === undefined) {
        return undefined;
    }
    if (!(input?.intervals?.length > 0)) {
        return null;
    }

    const separationThreshold = getChartLineSeperationThreshold(input.intervals);
    const data: [number, number][] = [];
    for (const [a, b] of input.intervals.pairs()) {
        const valueA = a.payload;
        const valueB = b.payload;

        data.push([a.end.valueOf(), valueA]);
        if (Math.abs(a.end.getTime() - b.begin.getTime()) >= separationThreshold) {
            data.push([a.end.valueOf(), 0]);
            data.push([b.begin.valueOf(), 0]);
            data.push([b.begin.valueOf(), valueB]);
        }
    }

    const last = input.intervals.at(-1);
    const lastValue = last.payload;
    data.push([last.end.valueOf(), lastValue]);

    if (data.length === 1) {
        data.unshift([input.intervals[0].begin.valueOf(), data[0][1]]);
    }

    const series: Array<Highcharts.SeriesOptionsType> = [];
    chartType === 'line'
        ? series.push({
            type: 'spline',
            name: 'Payload',
            color: 'lightgreen',
            data,
        })
        : series.push({
            type: 'column',
            name: 'Payload',
            color: 'lightgreen',
            data,
        });

    let options: Highcharts.Options = {
        rangeSelector: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        chart: {
            backgroundColor: 'transparent',
            zooming: {
                mouseWheel: {
                    enabled: zoomingEnable,
                },
            },
        },
        exporting: {
            enabled: false,
        },
        navigator: {
            enabled: navigatorEnable,
        },
        plotOptions: {
            series: {
                showInNavigator: navigatorEnable,
            },
        },
        time: {
            timezoneOffset: new Date().getTimezoneOffset(),
        },
        scrollbar: CommonChartConfig.scrollbar(scrollbarEnable),
        tooltip: {
            enabled: tooltipEnable,
        },
        xAxis: {
            title: {
                text: 'Time',
                style: {
                    fontSize: '1em',
                    color: CHART_LABEL_COLOR,
                },
            },
            labels: {
                style: {
                    color: CHART_LABEL_COLOR,
                },
            },
            breaks: [
                {
                    breakSize: 1000,
                },
            ],
            events: {
                afterSetExtremes: function (event) {
                    if (event.trigger && event.userMin === event.dataMin) {
                        addData('begin');
                    } else if (event.trigger && event.dataMax === event.userMax) {
                        addData('end');
                    }
                },
            },
        },
        yAxis: CommonChartConfig.yAxis(title),
        legend: CommonChartConfig.legend(),
        series: series,
    };

    return options;
};

export const generateScoreTimelineChartLines = (
    chartType: 'series' | 'line',
    input: fragQuery.timeline.quantity.Response,
    addData: (type: string) => void,
    title: string,
    zoomingEnable: boolean,
    navigatorEnable: boolean,
    scrollbarEnable: boolean,
    tooltipEnable: boolean
): Highcharts.Options => {
    if (input === undefined) {
        return undefined;
    }
    if (!(input?.intervals?.length > 0)) {
        return null;
    }

    const separationThreshold = getChartLineSeperationThreshold(input.intervals);
    const scoreData: [number, number][] = [];
    const iqaScoreData: [number, number][] = [];

    let minValue = Number.MAX_VALUE;
    let maxValue = Number.MIN_VALUE;

    for (const [a, b] of input.intervals.pairs()) {
        const valueAScore = asFiniteNumber((a.score * 100).toFixed(2));
        const valueBScore = asFiniteNumber((b.score * 100).toFixed(2));
        const valueAIQAScore = asFiniteNumber((a.iqaScore * 100).toFixed(2));
        const valueBIQAScore = asFiniteNumber((b.iqaScore * 100).toFixed(2));

        minValue = Math.min(minValue, valueAScore, valueBScore, valueAIQAScore, valueAIQAScore);
        maxValue = Math.max(maxValue, valueAScore, valueBScore, valueAIQAScore, valueBIQAScore);

        scoreData.push([a.end.valueOf(), valueAScore]);
        iqaScoreData.push([a.end.valueOf(), valueAIQAScore]);
        if (Math.abs(a.end.getTime() - b.begin.getTime()) >= separationThreshold) {
            scoreData.push([a.end.valueOf(), 0]);
            scoreData.push([b.begin.valueOf(), 0]);
            iqaScoreData.push([a.end.valueOf(), 0]);
            iqaScoreData.push([b.begin.valueOf(), 0]);
            scoreData.push([b.begin.valueOf(), valueBScore]);
            iqaScoreData.push([b.begin.valueOf(), valueBIQAScore]);
        }
    }

    const last = input.intervals.at(-1);
    const lastScoreValue = asFiniteNumber((last.score * 100).toFixed(2));
    const lastiqaScoreValue = asFiniteNumber((last.iqaScore * 100).toFixed(2));

    minValue = Math.min(minValue, lastiqaScoreValue, lastScoreValue);
    maxValue = Math.max(maxValue, lastiqaScoreValue, lastScoreValue);

    scoreData.push([last.end.valueOf(), lastScoreValue]);
    iqaScoreData.push([last.end.valueOf(), lastiqaScoreValue]);

    if (scoreData.length === 1) {
        scoreData.unshift([input.intervals[0].begin.valueOf(), scoreData[0][1]]);
    }

    const series: Array<Highcharts.SeriesOptionsType> = [];
    chartType === 'line'
        ? series.push({
            type: 'spline',
            name: 'AI Score',
            color: 'red',
            data: scoreData,
        }, {
            type: 'spline',
            name: 'Image Quality',
            color: 'blue',
            data: iqaScoreData
        })
        : series.push({
            type: 'column',
            name: 'AI Score',
            color: 'red',
            data: scoreData,
        }, {
            type: 'column',
            name: 'Image Quality',
            color: 'blue',
            data: iqaScoreData
        });

    let options: Highcharts.Options = {
        rangeSelector: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        chart: {
            backgroundColor: 'transparent',
            zooming: {
                mouseWheel: {
                    enabled: zoomingEnable,
                },
            },
        },
        exporting: {
            enabled: false,
        },
        navigator: {
            enabled: navigatorEnable,
        },
        plotOptions: {
            series: {
                showInNavigator: navigatorEnable,
            },
        },
        time: {
            timezoneOffset: new Date().getTimezoneOffset(),
        },
        scrollbar: CommonChartConfig.scrollbar(scrollbarEnable),
        tooltip: {
            enabled: tooltipEnable,
        },
        xAxis: {
            title: {
                text: 'Time',
                style: {
                    fontSize: '1em',
                    color: CHART_LABEL_COLOR,
                },
            },
            labels: {
                style: {
                    color: CHART_LABEL_COLOR,
                },
            },
            breaks: [
                {
                    breakSize: 1000,
                },
            ],
            events: {
                afterSetExtremes: function (event) {
                    if (event.trigger && event.userMin === event.dataMin) {
                        addData('begin');
                    } else if (event.trigger && event.dataMax === event.userMax) {
                        addData('end');
                    }
                },
            },
        },

        yAxis: {
            title: {
                text: `${title}`,
                style: {
                    fontSize: '1em',
                    color: CHART_LABEL_COLOR,
                },
            },
            labels: {
                style: {
                    color: CHART_LABEL_COLOR,
                },
            },
            tickAmount: 8,
            opposite: false,
            min: minValue - 2,
            max: maxValue + 2,
        },
        legend: CommonChartConfig.legend(),
        series: series,
    };

    return options;
};

export const generateMergedTimelineChartLines = (
    percents: Map<number, string>,
    input: fragQuery.timeline.sieveSizesAtPercents.Response,
    inputScore: fragQuery.timeline.quantity.Response,
    particleSizeUnit: string,
    addData: (type: string) => void,
    plotClick: (time: number) => void
): Highcharts.Options => {
    if (
        !input ||
        !inputScore ||
        !input.intervals?.length ||
        !inputScore.intervals?.length
    ) {
        return null;
    }

    const scoreData: [number, number][] = [];
    const iqaScoreData: [number, number][] = [];
    const particleSeries: Array<Highcharts.SeriesOptionsType> = [];
    const scoreSeries: Array<Highcharts.SeriesOptionsType> = [];

    const separationThreshold = getChartLineSeperationThreshold(input.intervals);

    particleSeries.push(
        ...input.percents
            .sort((a, b) => a - b)
            .map((percent, i): Highcharts.SeriesOptionsType => {
                if (!percents.has(percent)) return null;

                const data: [number, number][] = [];
                let prevInterval: fragQuery.timeline.sieveSizesAtPercents.Interval;
                for (const interval of input.intervals) {
                    const y = interval.data[i] ?? 0;
                    if (!prevInterval) {
                        data.push([interval.begin.valueOf(), y]);
                    } else if (
                        prevInterval.end.getTime() - interval.begin.getTime() >=
                        separationThreshold
                    ) {
                        data.push([prevInterval.end.valueOf(), 0]);
                        data.push([interval.begin.valueOf(), 0]);
                    }
                    data.push([interval.begin.lerp(interval.end, 0.5).valueOf(), y]);
                    prevInterval = interval;
                }

                return {
                    type: 'spline',
                    name: 'D' + percent,
                    data,
                    color: percents.get(percent),
                    lineWidth: 4,
                    yAxis: 1,
                    point: {
                        events: {
                            click: function (event) {
                                if (plotClick) {
                                    const clickedXValue = this.series.xAxis.toValue(event.chartX);
                                    plotClick(clickedXValue);
                                }
                            },
                        },
                    },
                };
            })
            .filter((v) => !!v)
            .reverse()
    );

    let minScore = Infinity;
    let maxScore = -Infinity;

    for (const interval of inputScore.intervals) {
        const score = asFiniteNumber((interval.score * 100).toFixed(2));
        const iqaScore = asFiniteNumber((interval.iqaScore * 100).toFixed(2));

        scoreData.push([interval.begin.valueOf(), score]);
        iqaScoreData.push([interval.begin.valueOf(), iqaScore]);

        minScore = Math.min(minScore, score, iqaScore);
        maxScore = Math.max(maxScore, score, iqaScore);
    }

    if (minScore === Infinity) {
        minScore = 0;
    }
    if (maxScore === -Infinity) {
        maxScore = 0;
    }

    scoreSeries.push({
        type: 'spline',
        name: 'AI Score',
        color: 'red',
        data: scoreData,
        yAxis: 0,
    });

    scoreSeries.push({
        type: 'spline',
        name: 'Image Quality',
        color: 'blue',
        data: iqaScoreData,
        yAxis: 0,
    })

    return {
        rangeSelector: { enabled: false },
        credits: { enabled: false },
        chart: {
            backgroundColor: 'transparent',
            zooming: { mouseWheel: { enabled: true } },
        },
        exporting: { enabled: false },
        navigator: { enabled: true },
        scrollbar: {
            enabled: true,
            liveRedraw: false,
            barBackgroundColor: PRIMARY_COLOR,
        },
        tooltip: { enabled: true },
        xAxis: {
            title: { text: 'Time', style: { fontSize: '1em', color: CHART_LABEL_COLOR } },
            labels: { style: { color: CHART_LABEL_COLOR } },
            breaks: [{ breakSize: 1000 }],
            events: {
                afterSetExtremes: function (event) {
                    if (event.trigger && event.userMin === event.dataMin) {
                        addData('begin');
                    } else if (event.trigger && event.dataMax === event.userMax) {
                        addData('end');
                    }
                },
            },
        },
        yAxis: [
            {
                title: { text: 'AI Score & Image Quality (%)', style: { fontSize: '1em', color: CHART_LABEL_COLOR } },
                labels: { style: { color: CHART_LABEL_COLOR } },
                opposite: false,
                min: minScore - 2,
                max: maxScore + 2,
            },
            {
                title: {
                    text: `Particle Size (${particleSizeUnit})`,
                    style: { fontSize: '1em', color: CHART_LABEL_COLOR },
                },
                labels: { style: { color: CHART_LABEL_COLOR } },
                opposite: true,
            },
        ],
        legend: {
            enabled: true,
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
            itemStyle: { fontSize: '12px', color: CHART_TEXT_COLOR },
            symbolHeight: 12,
            symbolWidth: 30,
            symbolRadius: 20,
        },
        series: [...scoreSeries, ...particleSeries],
    };
};

const _generateTooltipContent = (
    interval: fragQuery.timeline.sieveSizesAtPercents.Interval,
    isConveyorDevice: boolean,
    muckpiles: muckpileQuery.list.MuckpileInfo[] | undefined,
    vehicles: Vehicle[] | undefined,
    onlyPlaceholder: boolean = false
): string => {
    if (!interval) return "<div>--</div>";

    const containerId = `tooltip-${interval.begin.valueOf()}-${interval.muckpileId ?? 0}-${interval.vehicleId ?? 0}`;

    const placeholderHTML = !isConveyorDevice
        ? `
            <div><strong>Muckpile:</strong> ${muckpiles?.find(m => m.id === interval?.muckpileId)?.name ?? "--"}</div>
            <div><strong>Vehicle:</strong> ${vehicles?.find(v => v.id === interval?.vehicleId)?.name ?? "--"}</div>
            <div style="width:300px; height:200px; display:flex; align-items:center; justify-content:center; background:#f0f0f0; color:#999;">Loading image...</div>
          `
        : `<div style="width:300px; height:200px; display:flex; align-items:center; justify-content:center; background:#f0f0f0; color:#999;">Loading image...</div>`;

    if (onlyPlaceholder) {
        return `<div id="${containerId}">${placeholderHTML}</div>`;
    }

    setTimeout(async () => {
        let imageUrl = interval.imageUrl ?? "";
        const isTiffFile = imageUrl.split('?')[0].toLowerCase().endsWith('.tif') || imageUrl.split('?')[0].toLowerCase().endsWith('.tiff');

        if (isTiffFile) {
            try {
                const { mergedUrl } = await TiffToImageConverter.getTiffImageUrls(interval.imageUrl);
                imageUrl = mergedUrl;
            } catch (e) {
                console.error("Failed to load TIFF:", e);
                imageUrl = "../../../../assets/image_not_found.jpg";
            }
        }

        const el = document.getElementById(containerId);
        if (el) {
            el.innerHTML = !isConveyorDevice
                ? `
                    <div><strong>Muckpile:</strong> ${muckpiles?.find(m => m.id === interval?.muckpileId)?.name ?? "--"}</div>
                    <div><strong>Vehicle:</strong> ${vehicles?.find(v => v.id === interval?.vehicleId)?.name ?? "--"}</div>
                    <img src="${imageUrl}" onerror="this.src='../../../../assets/image_not_found.jpg'" height="200" width="300"/>
                  `
                : `<img src="${imageUrl}" onerror="this.src='../../../../assets/image_not_found.jpg'" height="200" width="300"/>`;
        }
    }, 0);

    return `<div id="${containerId}">${placeholderHTML}</div>`;
};

const _createTooltipContentHtml = (
    interval: fragQuery.timeline.sieveSizesAtPercents.Interval,
    chart_type: string,
    points: TooltipFormatterContextObject['points'],
    onlyPlaceholder: boolean = false
): string => {
    const imageContainerId = `tooltip-${interval.begin.valueOf()}-${interval.id ?? 0}`;
    const total_series = interval.customSizePercents.length;
    const symbol = '●';
    const dataHtml = points
        .map((point, index) => {
            if (!point.series.chart.legend.group) return null;
            const legendSymbol = `<span width='20' height='20' style="font-size:20px;color:${point.color}">${symbol}</span>`;
            const seriesName = point.series.name;
            const percent = point.y.toFixed(2);
            const cummulative_percent = interval.customSizePercents[total_series - 1 - point.series.index];
            return `<span>${legendSymbol} </span><b>${seriesName}</b>: ${chart_type == 'column' ? cummulative_percent : percent}%`;
        })
        .reduce((acc, curr, index, array) => {
            if (index % 2 === 0) {
                acc.push(curr + (array[index + 1] ? ', ' + array[index + 1] : ''));
            }
            return acc;
        }, [])
        .map((line) => `<p style="margin: 0;">${line}</p>`)
        .join('');
    const html = `
                 <div>
                     <b>Start Time:</b> ${interval.begin.toLocaleString()}
                 </div>
                 <div style='display:flex'>
                     <div>${dataHtml}</div>
                     <div id="${imageContainerId}"
                          style="width:150px; height:100px; display:flex; align-items:center; justify-content:center; background:#f0f0f0; color:#999; margin-left:10px;">
                          Loading image...
                     </div>
                 </div>
                `;

    if (onlyPlaceholder) {
        return html;
    }

    // The tooltip must load the image asynchronously because the Highcharts formatter function must immediately return a string,
    //  and the image fetching and complex TIFF conversion are time-consuming asynchronous operations that would otherwise freeze the chart
    setTimeout(async () => {
        let imageUrl = interval.imageUrl ?? "";
        const isTiffFile = imageUrl.split('?')[0].toLowerCase().endsWith('.tif') || imageUrl.split('?')[0].toLowerCase().endsWith('.tiff');

        if (isTiffFile) {
            try {
                const { mergedUrl } = await TiffToImageConverter.getTiffImageUrls(interval.imageUrl);
                imageUrl = mergedUrl;
            } catch (e) {
                console.error("Failed to load TIFF:", e);
                imageUrl = "../../../../assets/image_not_found.jpg";
            }
        }

        const el = document.getElementById(imageContainerId);
        if (el) {
            el.innerHTML = `
                            <img src="${imageUrl}" 
                                 onerror="this.src='../../../../assets/image_not_found.jpg'" 
                                 height="100" width="150" 
                                 style="object-fit:cover; border-radius:4px;"/>
                             `;
        }
    }, 0);

    return html;
};

const _createOversizeRockTooltipContent = (
    interval: fragQuery.timeline.sieveSizesAtPercents.Interval,
    onlyPlaceholder: boolean = false
): string => {
    if (!interval) return "<div>--</div>";

    const containerId = `tooltip-${interval.begin.valueOf()}-${interval.muckpileId ?? 0}-${interval.vehicleId ?? 0}`;
    const placeholderHTML = `<div><strong>Oversize Rock Count:</strong> ${interval.oversizecount ?? "--"}</div>
                             <div style="width:200px; height:150px; display:flex; align-items:center; justify-content:center; background:#f0f0f0; color:#999; margin-top:5px">Loading image...</div>`;

    if (onlyPlaceholder) {
        return `<div id="${containerId}">${placeholderHTML}</div>`;
    }

    setTimeout(async () => {
        let imageUrl = interval.imageUrl ?? "";
        const isTiffFile = imageUrl.split('?')[0].toLowerCase().endsWith('.tif') || imageUrl.split('?')[0].toLowerCase().endsWith('.tiff');

        if (isTiffFile) {
            try {
                const { mergedUrl } = await TiffToImageConverter.getTiffImageUrls(interval.imageUrl);
                imageUrl = mergedUrl;
            } catch (e) {
                console.error("Failed to load TIFF:", e);
                imageUrl = "../../../../assets/image_not_found.jpg";
            }
        }

        const el = document.getElementById(containerId);
        if (el) {
            el.innerHTML = `<div><strong>Oversize Rock Count:</strong> ${interval.oversizecount ?? "--"}</div>
                            <img src="${imageUrl}" onerror="this.src='../../../../assets/image_not_found.jpg'" height="150" width="200" style="margin-top:5px"/>`;
        }
    }, 0);

    return `<div id="${containerId}">${placeholderHTML}</div>`;
};